// src/components/draft/DraftRosterPanel.js - Roster panel during live draft
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  ChevronDown, ChevronUp, User, Users, Shield, Heart, ArrowLeftRight, RefreshCw
} from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import PlayerLink from '../PlayerLink';
import { isPitcherPosition } from '../../utils/playerClassification';

// Position eligibility mapping (same as backend)
const POSITION_ELIGIBILITY = {
  "C": ["C", "UTIL"],
  "1B": ["1B", "CI", "UTIL"],
  "2B": ["2B", "MI", "UTIL"],
  "3B": ["3B", "CI", "UTIL"],
  "SS": ["SS", "MI", "UTIL"],
  "LF": ["OF", "UTIL"],
  "CF": ["OF", "UTIL"],
  "RF": ["OF", "UTIL"],
  "OF": ["OF", "UTIL"],
  "DH": ["UTIL"],
  "SP": ["P"],
  "RP": ["P"],
  "P": ["P"]
};

const getEligiblePositions = (position) => {
  if (!position) return [];
  return POSITION_ELIGIBILITY[position] || [];
};

const DraftRosterPanel = ({
  league,
  userTeam,
  draftState,
  selectedPlayer,
  onSlotClick,
  isYourTurn,
  timerData,
  cashBalance,
  allTeams = [],
  onOfferTrade
}) => {
  const [showBench, setShowBench] = useState(false);
  const [showMinors, setShowMinors] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(null); // null = my team
  const [dbRoster, setDbRoster] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const isViewingOtherTeam = selectedTeamId && selectedTeamId !== userTeam?.team_id;
  const viewedTeamId = selectedTeamId || userTeam?.team_id;

  const leagueId = league?.league_id;
  const refreshRoster = useCallback(async () => {
    if (!leagueId || !viewedTeamId) return;
    setRefreshing(true);
    try {
      const response = await leaguesAPI.getTeamRosterCanonical(leagueId, viewedTeamId);
      const players = response?.players || response?.roster || (Array.isArray(response) ? response : []);
      setDbRoster(players);
    } catch (err) {
      console.error('Failed to refresh roster:', err);
    } finally {
      setRefreshing(false);
    }
  }, [leagueId, viewedTeamId]);

  // Auto-load roster on mount, when viewed team changes, and poll every 5s during draft
  const picksMade = draftState?.picksMade || 0;
  useEffect(() => {
    refreshRoster();
  }, [refreshRoster, picksMade]);

  useEffect(() => {
    if (!draftState || draftState.status === 'completed') return;
    const interval = setInterval(refreshRoster, 5000);
    return () => clearInterval(interval);
  }, [refreshRoster, draftState?.status]);

  // Generate roster slots from league position requirements
  const rosterSlots = useMemo(() => {
    if (!league?.roster_settings?.position_requirements) return { hitting: [], pitching: [], bench: [], minors: [] };

    const positionRequirements = league.roster_settings.position_requirements;
    const batterPositions = ['C', '1B', '2B', 'SS', '3B', 'MI', 'CI', 'OF', 'UTIL'];
    const pitcherPositions = ['P'];

    const batters = [];
    const pitchers = [];

    batterPositions.forEach(pos => {
      const config = positionRequirements[pos];
      if (config?.slots > 0) {
        for (let i = 0; i < config.slots; i++) {
          batters.push({ slotId: `${pos}_${i}`, position: pos, index: i });
        }
      }
    });

    pitcherPositions.forEach(pos => {
      const config = positionRequirements[pos];
      if (config?.slots > 0) {
        for (let i = 0; i < config.slots; i++) {
          pitchers.push({ slotId: `${pos}_${i}`, position: pos, index: i });
        }
      }
    });

    const benchSlots = [];
    const benchCount = league.roster_settings?.bench_slots || 0;
    for (let i = 0; i < benchCount; i++) {
      benchSlots.push({ slotId: `bench_${i}`, position: 'bench', index: i });
    }

    const minorsSlots = [];
    const minorsCount = league.roster_settings?.minor_league_slots || 0;
    for (let i = 0; i < minorsCount; i++) {
      minorsSlots.push({ slotId: `minors_${i}`, position: 'minors', index: i });
    }

    return { hitting: batters, pitching: pitchers, bench: benchSlots, minors: minorsSlots };
  }, [league]);

  // Viewed team's drafted players (works for both own team and other teams)
  const viewedTeamPlayers = useMemo(() => {
    if (!draftState?.picks || !viewedTeamId) return [];
    return draftState.picks.filter(pick =>
      pick.current_owner_team_id === viewedTeamId && pick.league_player_id
    );
  }, [draftState?.picks, viewedTeamId]);

  // Map players to roster slots: DB roster first (pre-draft), then draft picks overlay
  // After a refresh, DB positions are authoritative. Draft state positions are only
  // used for players NOT in the DB roster (newly drafted players not yet persisted).
  const filledSlots = useMemo(() => {
    const slotMap = {};
    const placedPlayerIds = new Set();

    // First pass: DB roster (authoritative after refresh)
    // Place active players in slots, and mark ALL players (including DL/bench/minors)
    // as "placed" so the draft picks overlay doesn't re-add stale versions
    if (dbRoster && dbRoster.length > 0) {
      dbRoster.forEach(player => {
        const pos = player.league_context?.roster?.position;
        const status = player.league_context?.roster?.status;
        const playerId = player.ids?.league_player;
        if (playerId) {
          placedPlayerIds.add(playerId); // Mark ALL DB players as handled
        }
        if (pos && playerId && status === 'active') {
          slotMap[pos] = { player, source: 'db' };
        }
      });
    }

    // Second pass: Draft picks — skip players already placed from DB
    // NOTE: Draft pick objects are NOT canonical player objects (Bible Section 4 exception).
    // The player ID lives at pick.league_player_id (flat), not pick.player.ids.league_player.
    const unplacedPicks = [];
    viewedTeamPlayers.forEach(pick => {
      const rosterPosition = pick.roster_position;
      const playerId = pick.league_player_id;

      if (playerId && placedPlayerIds.has(playerId)) return;

      if (rosterPosition && rosterPosition !== 'null') {
        slotMap[rosterPosition] = { player: pick.player, pick, source: 'draft' };
      } else if (playerId) {
        unplacedPicks.push(pick);
      }
    });

    // Third pass: Auto-assign unplaced players (e.g., after a trade resets position to null).
    // Try eligible active slot first, then bench — matches backend find_available_position_slot logic.
    unplacedPicks.forEach(pick => {
      const playerPos = pick.player?.info?.position;
      const eligible = playerPos ? (POSITION_ELIGIBILITY[playerPos] || []) : [];
      let placed = false;

      // Try active slots: natural position first, then composite/flex
      for (const slotType of eligible) {
        const posConfig = league?.roster_settings?.position_requirements?.[slotType];
        const slotCount = posConfig?.slots || 0;
        for (let i = 0; i < slotCount; i++) {
          const slotId = `${slotType}_${i}`;
          if (!slotMap[slotId]) {
            slotMap[slotId] = { player: pick.player, pick, source: 'draft' };
            placed = true;
            break;
          }
        }
        if (placed) break;
      }

      // Fall back to first open bench slot
      if (!placed) {
        const benchCount = league?.roster_settings?.bench_slots || 0;
        for (let i = 0; i < benchCount; i++) {
          const slotId = `bench_${i}`;
          if (!slotMap[slotId]) {
            slotMap[slotId] = { player: pick.player, pick, source: 'draft' };
            break;
          }
        }
      }
    });

    return slotMap;
  }, [viewedTeamPlayers, dbRoster, league]);

  // Count players by roster status
  const statusCounts = useMemo(() => {
    const counts = { active: 0, bench: 0, minors: 0, dl: 0, unassigned: 0 };
    viewedTeamPlayers.forEach(pick => {
      if (!pick.roster_position && !pick.roster_status) {
        counts.unassigned++;
      } else {
        const status = pick.roster_status || 'active';
        counts[status] = (counts[status] || 0) + 1;
      }
    });
    return counts;
  }, [viewedTeamPlayers]);

  // Calculate total salary spent (draft picks + DB-sourced traded-in players)
  const totalSalary = useMemo(() => {
    const draftTotal = viewedTeamPlayers.reduce((sum, pick) => {
      return sum + Math.round(Number(pick.player?.financial?.baseline_price || 0));
    }, 0);
    if (dbRoster) {
      const draftPlayerIds = new Set(viewedTeamPlayers.map(p => p.league_player_id));
      const dbExtra = dbRoster.reduce((sum, player) => {
        const lpId = player.ids?.league_player;
        if (lpId && !draftPlayerIds.has(lpId)) {
          return sum + Math.round(Number(player.league_context?.financial?.contract_salary || 0));
        }
        return sum;
      }, 0);
      return draftTotal + dbExtra;
    }
    return draftTotal;
  }, [viewedTeamPlayers, dbRoster]);

  // Detect if the user's draft is complete (all remaining picks are skipped)
  const isUserDraftComplete = useMemo(() => {
    if (!draftState?.picks || !userTeam?.team_id || isViewingOtherTeam) return false;
    const currentPickNum = draftState.currentPick?.overall_pick_number || Infinity;
    const myPicks = draftState.picks.filter(p => p.current_owner_team_id === userTeam.team_id);
    const myRemainingPicks = myPicks.filter(p =>
      !p.league_player_id && !p.player?.info?.full_name && (p.overall_pick_number || 0) >= currentPickNum
    );
    // If no remaining picks at all, draft is complete
    if (myPicks.length > 0 && myRemainingPicks.length === 0) {
      const hasAnyUnpicked = myPicks.some(p => !p.league_player_id && !p.player?.info?.full_name);
      return !hasAnyUnpicked;
    }
    return false;
  }, [draftState?.picks, draftState?.currentPick, userTeam?.team_id, isViewingOtherTeam]);

  // Get unassigned players
  const unassignedPlayers = useMemo(() => {
    return viewedTeamPlayers.filter(pick => !pick.roster_position && !pick.roster_status);
  }, [viewedTeamPlayers]);

  // Team options with budget info
  const teamOptions = useMemo(() => {
    if (!allTeams.length || !draftState?.picks) return [];
    const draftCap = league?.salary_settings?.draft_cap || 600;

    return allTeams
      .filter(t => t.team_id !== userTeam?.team_id)
      .map(team => {
        const spent = draftState.picks
          .filter(p => p.current_owner_team_id === team.team_id && p.league_player_id)
          .reduce((sum, p) => sum + Math.round(Number(p.player?.financial?.baseline_price || p.salary || 0)), 0);
        return { ...team, spent, remaining: Math.round(draftCap - spent) };
      })
      .sort((a, b) => a.team_name.localeCompare(b.team_name));
  }, [allTeams, draftState?.picks, userTeam?.team_id, league?.salary_settings?.draft_cap]);

  // Get selected player's eligible positions for highlighting (own team only)
  const eligibleSlotPositions = useMemo(() => {
    if (!selectedPlayer || isViewingOtherTeam) return new Set();

    const playerPosition = selectedPlayer.info?.position;
    if (!playerPosition) return new Set();

    const eligible = new Set();
    const directEligible = getEligiblePositions(playerPosition);
    directEligible.forEach(pos => eligible.add(pos));

    if (playerPosition === '2B' || playerPosition === 'SS') eligible.add('MI');
    if (playerPosition === '1B' || playerPosition === '3B') eligible.add('CI');
    if (['LF', 'CF', 'RF', 'OF'].includes(playerPosition)) eligible.add('OF');
    if (isPitcherPosition(playerPosition)) eligible.add('P');
    if (!isPitcherPosition(playerPosition)) eligible.add('UTIL');
    eligible.add('bench');
    eligible.add('minors');

    return eligible;
  }, [selectedPlayer, isViewingOtherTeam]);

  // Render a single roster slot
  const renderSlot = (slot) => {
    const filled = filledSlots[slot.slotId];
    const isEligible = !isViewingOtherTeam && selectedPlayer && eligibleSlotPositions.has(slot.position);
    const canClick = !isViewingOtherTeam && isYourTurn && selectedPlayer && isEligible && !filled;

    const handleClick = () => {
      if (canClick && onSlotClick) {
        onSlotClick(slot.slotId, slot.position);
      }
    };

    const salary = filled ? Math.round(Number(
      filled.source === 'db'
        ? filled.player?.league_context?.financial?.contract_salary
        : filled.player?.financial?.baseline_price
    ) || 0) : null;

    return (
      <div
        key={slot.slotId}
        onClick={handleClick}
        className={`
          flex items-center gap-2 px-2 py-1.5 rounded border transition-all
          ${filled
            ? `${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.border.neutral}`
            : isEligible && selectedPlayer
              ? `border-yellow-400/60 bg-yellow-400/10 ${canClick ? 'cursor-pointer hover:bg-yellow-400/20' : ''}`
              : `${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.border.neutralLight} opacity-50`
          }
        `}
      >
        {/* Position Badge */}
        <div className={`
          w-9 h-6 flex items-center justify-center rounded text-[11px] font-bold
          ${filled
            ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black}`
            : isEligible && selectedPlayer
              ? 'bg-yellow-400/30 text-yellow-400'
              : 'bg-neutral-700 text-neutral-400'
          }
        `}>
          {slot.position === 'bench' ? 'BN' :
           slot.position === 'minors' ? 'Mi' :
           slot.position}
        </div>

        {/* Player Name */}
        <div className="flex-1 min-w-0">
          {filled ? (
            <PlayerLink mlbId={filled.player?.ids?.mlb || null} name={filled.player?.info?.full_name || 'Unknown'} style={{ fontSize: '0.75rem', color: '#f9fafb' }} />
          ) : (
            <span className={`text-[11px] ${isEligible && selectedPlayer ? 'text-yellow-400' : dynastyTheme.classes.text.neutralLight}`}>
              Empty
            </span>
          )}
        </div>

        {/* Salary */}
        <div className={`w-10 text-right text-[11px] ${filled ? dynastyTheme.classes.text.primary : dynastyTheme.classes.text.neutralDark}`}>
          {salary !== null ? `$${salary}` : '-'}
        </div>

        {/* Trade button (other teams only, filled slots only) */}
        {isViewingOtherTeam && filled && onOfferTrade && (
          <button
            onClick={(e) => { e.stopPropagation(); onOfferTrade(selectedTeamId, filled.pick?.league_player_id); }}
            className="p-1 rounded hover:bg-yellow-500/20 transition-colors flex-shrink-0"
            title="Offer Trade"
          >
            <ArrowLeftRight className="w-3 h-3 text-yellow-500" />
          </button>
        )}
      </div>
    );
  };

  // Calculate totals
  const totalBatterSlots = rosterSlots.hitting.length;
  const totalPitcherSlots = rosterSlots.pitching.length;
  const filledBatterSlots = rosterSlots.hitting.filter(s => filledSlots[s.slotId]).length;
  const filledPitcherSlots = rosterSlots.pitching.filter(s => filledSlots[s.slotId]).length;
  const benchMax = rosterSlots.bench.length;
  const minorsMax = rosterSlots.minors.length;

  const draftCap = league?.salary_settings?.draft_cap || 600;

  return (
    <div className={`${dynastyTheme.components.card.base} p-3 h-full overflow-y-auto`}>
      {/* Team selector */}
      <div className="mb-3">
        <select
          value={selectedTeamId || ''}
          onChange={(e) => setSelectedTeamId(e.target.value || null)}
          className={`w-full text-xs rounded px-2 py-1.5 ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} focus:outline-none`}
        >
          <option value="">My Roster</option>
          {teamOptions.map(t => (
            <option key={t.team_id} value={t.team_id}>
              {t.team_name} (${t.remaining})
            </option>
          ))}
        </select>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h2 className={`text-sm font-bold ${dynastyTheme.classes.text.white}`}>
            {isViewingOtherTeam
              ? teamOptions.find(t => t.team_id === selectedTeamId)?.team_name || 'Team'
              : 'Your Roster'
            }
          </h2>
          <button
            onClick={refreshRoster}
            disabled={refreshing}
            className={`p-1 rounded transition-colors ${refreshing ? 'opacity-40' : 'opacity-60 hover:opacity-100 hover:bg-neutral-700/50'}`}
            title="Refresh roster positions from server"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${dynastyTheme.classes.text.neutralLight} ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className={`text-xs font-bold ${dynastyTheme.classes.text.primary}`}>
          ${totalSalary} / ${isViewingOtherTeam ? draftCap : (cashBalance !== null ? Math.round(cashBalance) : '...')}
        </div>
      </div>

      {/* Draft Complete Indicator (own team only) */}
      {!isViewingOtherTeam && isUserDraftComplete && (
        <div className="mb-3 p-2.5 rounded-lg bg-green-500/15 border border-green-500/40">
          <div className="flex items-center gap-2">
            <span className="text-green-400 text-sm">✓</span>
            <span className="font-bold text-green-400 text-sm">DRAFT COMPLETE</span>
          </div>
          <div className={`mt-1 text-xs ${dynastyTheme.classes.text.neutralLight}`}>
            All your picks are filled. Remaining picks will be skipped.
          </div>
        </div>
      )}

      {/* On The Clock Indicator (own team only) */}
      {!isViewingOtherTeam && isYourTurn && !isUserDraftComplete && (
        <div className="mb-3 p-2.5 rounded-lg bg-yellow-500/20 border border-yellow-500">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500 animate-pulse" />
            <span className="font-bold text-yellow-500 text-sm">YOUR TURN</span>
            {timerData && (
              <span className="ml-auto text-yellow-500 font-mono text-sm">
                {Math.floor((timerData.expires_at - Date.now()) / 1000)}s
              </span>
            )}
          </div>
          {selectedPlayer && (
            <div className={`mt-2 text-xs ${dynastyTheme.classes.text.white}`}>
              Click slot to assign {selectedPlayer.info?.full_name || selectedPlayer.info?.last_name}
            </div>
          )}
        </div>
      )}

      {/* Batters Section */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <User className={`w-4 h-4 ${dynastyTheme.classes.text.primary}`} />
            <span className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>Batters</span>
          </div>
          <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
            {filledBatterSlots}/{totalBatterSlots}
          </span>
        </div>
        <div className="space-y-1">
          {rosterSlots.hitting.map(slot => renderSlot(slot))}
        </div>
      </div>

      {/* Pitchers Section */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <User className={`w-4 h-4 ${dynastyTheme.classes.text.info}`} />
            <span className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>Pitchers</span>
          </div>
          <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
            {filledPitcherSlots}/{totalPitcherSlots}
          </span>
        </div>
        <div className="space-y-1">
          {rosterSlots.pitching.map(slot => renderSlot(slot))}
        </div>
      </div>

      {/* Bench Section (Collapsible) */}
      {benchMax > 0 && (
        <div className="mb-3">
          <button
            onClick={() => setShowBench(!showBench)}
            className={`w-full flex items-center justify-between p-2 rounded ${dynastyTheme.classes.bg.darkLighter} hover:bg-neutral-700/50 transition-all`}
          >
            <div className="flex items-center gap-2">
              <Users className={`w-4 h-4 ${dynastyTheme.classes.text.neutralLight}`} />
              <span className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>Bench</span>
              <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                {statusCounts.bench}/{benchMax}
              </span>
            </div>
            {showBench ? (
              <ChevronUp className={`w-4 h-4 ${dynastyTheme.classes.text.neutralLight}`} />
            ) : (
              <ChevronDown className={`w-4 h-4 ${dynastyTheme.classes.text.neutralLight}`} />
            )}
          </button>
          {showBench && (
            <div className="mt-2 space-y-1">
              {rosterSlots.bench.map(slot => renderSlot(slot))}
            </div>
          )}
        </div>
      )}

      {/* Minors Section (Collapsible) */}
      {minorsMax > 0 && (
        <div className="mb-3">
          <button
            onClick={() => setShowMinors(!showMinors)}
            className={`w-full flex items-center justify-between p-2 rounded ${dynastyTheme.classes.bg.darkLighter} hover:bg-neutral-700/50 transition-all`}
          >
            <div className="flex items-center gap-2">
              <Shield className={`w-4 h-4 ${dynastyTheme.classes.text.warning}`} />
              <span className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>Minors</span>
              <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                {statusCounts.minors}/{minorsMax}
              </span>
            </div>
            {showMinors ? (
              <ChevronUp className={`w-4 h-4 ${dynastyTheme.classes.text.neutralLight}`} />
            ) : (
              <ChevronDown className={`w-4 h-4 ${dynastyTheme.classes.text.neutralLight}`} />
            )}
          </button>
          {showMinors && (
            <div className="mt-2 space-y-1">
              {rosterSlots.minors.map(slot => renderSlot(slot))}
            </div>
          )}
        </div>
      )}

      {/* Unassigned Players Section */}
      {unassignedPlayers.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-yellow-500/30 flex items-center justify-center">
                <span className="text-yellow-500 text-[10px] font-bold">!</span>
              </div>
              <span className={`text-sm font-medium text-yellow-500`}>Unassigned</span>
            </div>
            <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
              {unassignedPlayers.length}
            </span>
          </div>
          <div className="space-y-1">
            {unassignedPlayers.map((pick, idx) => (
              <div
                key={pick.league_player_id || idx}
                className={`flex items-center gap-2 px-2 py-1.5 rounded border border-yellow-500/30 bg-yellow-500/5`}
              >
                <div className={`w-9 h-6 flex items-center justify-center rounded text-xs font-bold bg-yellow-500/30 text-yellow-500`}>
                  {pick.player?.info?.position || '?'}
                </div>
                <div className="flex-1 min-w-0 flex items-center justify-between">
                  <PlayerLink mlbId={pick.player?.ids?.mlb || null} name={pick.player?.info?.full_name || 'Unknown'} style={{ fontWeight: 500, fontSize: '0.875rem', color: '#f9fafb' }} />
                  <span className={`text-xs ${dynastyTheme.classes.text.neutralLight} ml-1`}>
                    ${Math.round(Number(pick.player?.financial?.baseline_price) || 0)}
                  </span>
                </div>
                {isViewingOtherTeam && onOfferTrade && (
                  <button
                    onClick={() => onOfferTrade(selectedTeamId, pick.league_player_id)}
                    className="p-1 rounded hover:bg-yellow-500/20 transition-colors flex-shrink-0"
                    title="Offer Trade"
                  >
                    <ArrowLeftRight className="w-3 h-3 text-yellow-500" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftRosterPanel;
